// ===== K-Tec Workflow Store =====
// Order → Quotation → Invoice → Warranty pipeline
// localStorage key: ktec-workflow-store
// Switch USE_API=true to route through Laravel REST API.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  CustomerOrder, OrderStatus,
  WorkflowQuotation, QuotationStatus,
  WorkflowInvoice, InvoicePayment, PaymentStatus,
  WorkflowWarranty,
  LineItem,
  FinanceTransaction,
  StockMovement,
  AdminNotification,
} from '../types/workflow';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const genId = () => `KT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
const now = () => new Date().toISOString();

/** Sequential-style reference numbers */
const seqNum = (prefix: string, store?: string[]) => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const seq = String(Math.floor(Math.random() * 90000) + 10000);
  return `${prefix}-${year}${month}-${seq}`;
};

/** Generate warranty ID: KTEC-WR-YYYY-NNNNNN */
let _warrantyCounter = 0;
const genWarrantyId = () => {
  _warrantyCounter++;
  const year = new Date().getFullYear();
  const seq = String(_warrantyCounter).padStart(6, '0');
  return `KTEC-WR-${year}-${seq}`;
};

function calcLineTotal(item: Omit<LineItem, 'lineTotal'>): number {
  const base = item.quantity * item.unitPrice;
  if (!item.discountValue) return base;
  const disc = item.discountType === 'percentage' ? base * (item.discountValue / 100) : item.discountValue;
  return Math.max(0, base - disc);
}

function calcQuotationTotals(q: Pick<WorkflowQuotation,
  'lineItems' | 'overallDiscount' | 'overallDiscountType' | 'installationCost' | 'labourCost' | 'additionalCharges' | 'taxRate'
>) {
  const subtotal = q.lineItems.reduce((s, it) => s + it.lineTotal, 0);
  const itemDiscount = q.lineItems.reduce((s, it) => {
    const base = it.quantity * it.unitPrice;
    return s + (base - it.lineTotal);
  }, 0);
  const overallDiscAmt = q.overallDiscountType === 'percentage'
    ? subtotal * (q.overallDiscount / 100)
    : q.overallDiscount;
  const afterDisc = subtotal - overallDiscAmt;
  const beforeTax = afterDisc + q.installationCost + q.labourCost + q.additionalCharges;
  const taxAmount = beforeTax * (q.taxRate / 100);
  const grandTotal = beforeTax + taxAmount;
  // Internal material cost
  const totalMaterialCost = q.lineItems.reduce((s, it) => s + ((it.costPrice || 0) * it.quantity), 0);
  return { subtotal, itemDiscount, taxAmount, grandTotal, totalMaterialCost };
}

// ─── Interface ────────────────────────────────────────────────────────────────
interface WorkflowStore {
  // ── Orders
  orders: CustomerOrder[];
  addOrder: (order: Omit<CustomerOrder, 'id' | 'referenceNumber' | 'createdAt' | 'updatedAt'>) => string;
  updateOrderStatus: (id: string, status: OrderStatus, notes?: string) => void;
  deleteOrder: (id: string) => void;
  getOrderById: (id: string) => CustomerOrder | undefined;

  // ── Quotations
  quotations: WorkflowQuotation[];
  addQuotation: (q: Omit<WorkflowQuotation, 'id' | 'quotationNumber' | 'createdAt' | 'updatedAt'>) => string;
  updateQuotation: (id: string, data: Partial<WorkflowQuotation>) => void;
  deleteQuotation: (id: string) => void;
  approveQuotation: (id: string) => void;
  rejectQuotation: (id: string) => void;
  convertQuotationToInvoice: (quotationId: string) => string | null;
  createQuotationFromOrder: (orderId: string) => string | null;

  // ── Invoices
  invoices: WorkflowInvoice[];
  addInvoice: (inv: Omit<WorkflowInvoice, 'id' | 'invoiceNumber' | 'createdAt' | 'updatedAt'>) => string;
  updateInvoice: (id: string, data: Partial<WorkflowInvoice>) => void;
  addPaymentToInvoice: (invoiceId: string, payment: Omit<InvoicePayment, 'id'>) => void;
  markInvoiceComplete: (invoiceId: string) => void;
  confirmInstallation: (invoiceId: string) => void;

  // ── Warranties
  warranties: WorkflowWarranty[];
  activateWarranty: (invoiceId: string) => string | null;
  updateWarranty: (id: string, data: Partial<WorkflowWarranty>) => void;
  getWarrantyByVerifyId: (verifyId: string) => WorkflowWarranty | undefined;

  // ── Finance Transactions (auto-created on payment)
  financeTransactions: FinanceTransaction[];
  addFinanceTransaction: (tx: Omit<FinanceTransaction, 'id' | 'createdAt'>) => string;
  // Computed getters
  getTotalIncome: () => number;
  getTotalExpenses: () => number;
  getTotalMaterialCost: () => number;
  getNetProfit: () => number;

  // ── Stock Movements
  stockMovements: StockMovement[];
  addStockMovement: (mov: Omit<StockMovement, 'id' | 'createdAt'>) => void;

  // ── Notifications
  notifications: AdminNotification[];
  addNotification: (n: Omit<AdminNotification, 'id' | 'createdAt' | 'isRead'>) => void;
  markNotificationRead: (id: string) => void;
  clearAllNotifications: () => void;
  unreadCount: () => number;
  resetBusinessData: () => void;
}

// ─── Store ────────────────────────────────────────────────────────────────────
export const useWorkflowStore = create<WorkflowStore>()(
  persist(
    (set, get) => ({
      orders: [],
      quotations: [],
      invoices: [],
      warranties: [],
      financeTransactions: [],
      stockMovements: [],
      notifications: [],

      // ── ORDER ACTIONS ──────────────────────────────────────────────────────
      addOrder: (order) => {
        const newOrder: CustomerOrder = {
          ...order,
          id: genId(),
          referenceNumber: seqNum('ORD'),
          createdAt: now(),
          updatedAt: now(),
          status: 'new',
        };
        get().addNotification({
          type: 'new_order',
          title: '🛒 New Website Order',
          message: `${newOrder.customerName} (${newOrder.customerPhone}) ordered ${newOrder.itemName}`,
          orderId: newOrder.id,
        });
        set(s => ({ orders: [newOrder, ...s.orders] }));
        return newOrder.id;
      },

      updateOrderStatus: (orderId, status, notes) => {
        set(s => ({
          orders: s.orders.map(o =>
            o.id === orderId ? { ...o, status, adminNotes: notes ?? o.adminNotes, updatedAt: now() } : o
          ),
        }));
      },

      deleteOrder: (orderId) => {
        set(s => ({ orders: s.orders.filter(o => o.id !== orderId) }));
      },

      getOrderById: (orderId) => get().orders.find(o => o.id === orderId),

      // ── QUOTATION ACTIONS ──────────────────────────────────────────────────
      addQuotation: (q) => {
        const totals = calcQuotationTotals(q);
        const newQ: WorkflowQuotation = {
          ...q,
          ...totals,
          id: genId(),
          quotationNumber: seqNum('QT'),
          createdAt: now(),
          updatedAt: now(),
          status: q.status || 'draft',
        };
        set(s => ({ quotations: [newQ, ...s.quotations] }));
        // Update linked order
        if (q.orderId) {
          set(s => ({
            orders: s.orders.map(o => o.id === q.orderId
              ? { ...o, status: 'quotation_created', quotationId: newQ.id, updatedAt: now() }
              : o
            ),
          }));
        }
        get().addNotification({
          type: 'quotation_created',
          title: '📋 Quotation Created',
          message: `${newQ.quotationNumber} for ${newQ.customerName} — Rs. ${newQ.grandTotal.toLocaleString()}`,
          quotationId: newQ.id,
        });
        return newQ.id;
      },

      updateQuotation: (qId, data) => {
        set(s => ({
          quotations: s.quotations.map(q => {
            if (q.id !== qId) return q;
            const updated = { ...q, ...data, updatedAt: now() };
            if (data.lineItems !== undefined || data.overallDiscount !== undefined ||
                data.installationCost !== undefined || data.labourCost !== undefined ||
                data.additionalCharges !== undefined || data.taxRate !== undefined) {
              const totals = calcQuotationTotals(updated);
              return { ...updated, ...totals };
            }
            return updated;
          }),
        }));
      },

      deleteQuotation: (qId) => {
        set(s => ({ quotations: s.quotations.filter(q => q.id !== qId) }));
      },

      approveQuotation: (qId) => {
        const q = get().quotations.find(q => q.id === qId);
        set(s => ({
          quotations: s.quotations.map(q => q.id === qId ? { ...q, status: 'approved' as QuotationStatus, updatedAt: now() } : q),
        }));
        if (q) {
          get().addNotification({
            type: 'quotation_approved',
            title: '✅ Quotation Approved',
            message: `${q.quotationNumber} for ${q.customerName} has been approved.`,
            quotationId: qId,
          });
        }
      },

      rejectQuotation: (qId) => {
        set(s => ({
          quotations: s.quotations.map(q => q.id === qId ? { ...q, status: 'rejected' as QuotationStatus, updatedAt: now() } : q),
        }));
      },

      convertQuotationToInvoice: (qId) => {
        const q = get().quotations.find(q => q.id === qId);
        if (!q || q.status !== 'approved') return null;
        // Prevent duplicate
        if (q.invoiceId) return q.invoiceId;

        const newInvoice: WorkflowInvoice = {
          id: genId(),
          invoiceNumber: seqNum('INV'),
          quotationId: qId,
          orderId: q.orderId,
          customerId: q.customerId,
          customerName: q.customerName,
          customerPhone: q.customerPhone,
          customerEmail: q.customerEmail,
          customerAddress: q.customerAddress,
          lineItems: [...q.lineItems],
          subtotal: q.subtotal,
          itemDiscount: q.itemDiscount,
          overallDiscount: q.overallDiscount,
          overallDiscountType: q.overallDiscountType,
          installationCost: q.installationCost,
          labourCost: q.labourCost,
          additionalCharges: q.additionalCharges,
          taxRate: q.taxRate,
          taxAmount: q.taxAmount,
          grandTotal: q.grandTotal,
          totalPaid: 0,
          balance: q.grandTotal,
          paymentTerms: q.paymentTerms,
          notes: q.notes,
          termsAndConditions: q.termsAndConditions,
          productWarranty: q.productWarranty,
          serviceWarranty: q.serviceWarranty,
          invoiceStatus: 'draft',
          paymentStatus: 'unpaid',
          invoiceDate: now(),
          payments: [],
          stockDeducted: false,
          createdAt: now(),
          updatedAt: now(),
        };

        set(s => ({
          invoices: [newInvoice, ...s.invoices],
          quotations: s.quotations.map(qt => qt.id === qId
            ? { ...qt, status: 'converted' as QuotationStatus, invoiceId: newInvoice.id, updatedAt: now() }
            : qt
          ),
        }));

        // Update linked order
        if (q.orderId) {
          set(s => ({
            orders: s.orders.map(o => o.id === q.orderId
              ? { ...o, status: 'converted_to_invoice' as const, invoiceId: newInvoice.id, updatedAt: now() }
              : o
            ),
          }));
        }

        get().addNotification({
          type: 'invoice_created',
          title: '🧾 Invoice Created',
          message: `${newInvoice.invoiceNumber} for ${newInvoice.customerName} — Rs. ${newInvoice.grandTotal.toLocaleString()}`,
          invoiceId: newInvoice.id,
        });

        return newInvoice.id;
      },

      createQuotationFromOrder: (orderId) => {
        const order = get().orders.find(o => o.id === orderId);
        if (!order) return null;

        const lineItem: LineItem = {
          id: genId(),
          itemType: order.itemType,
          ...(order.itemType === 'product' ? { productId: order.itemId } : { packageId: order.itemId }),
          name: order.itemName,
          quantity: order.quantity,
          unitPrice: order.itemPrice,
          lineTotal: order.quantity * order.itemPrice,
        };

        return get().addQuotation({
          orderId,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          customerEmail: order.customerEmail,
          customerAddress: order.customerAddress,
          lineItems: [lineItem],
          subtotal: lineItem.lineTotal,
          itemDiscount: 0,
          overallDiscount: 0,
          overallDiscountType: 'fixed',
          installationCost: 0,
          labourCost: 0,
          additionalCharges: 0,
          taxRate: 0,
          taxAmount: 0,
          grandTotal: lineItem.lineTotal,
          notes: order.message,
          status: 'draft',
        });
      },

      // ── INVOICE ACTIONS ────────────────────────────────────────────────────
      addInvoice: (inv) => {
        const newInv: WorkflowInvoice = {
          ...inv,
          id: genId(),
          invoiceNumber: seqNum('INV'),
          createdAt: now(),
          updatedAt: now(),
          payments: inv.payments ?? [],
          totalPaid: inv.totalPaid ?? 0,
          balance: inv.balance ?? inv.grandTotal,
          stockDeducted: false,
        };
        set(s => ({ invoices: [newInv, ...s.invoices] }));
        get().addNotification({
          type: 'invoice_created',
          title: '🧾 Invoice Created',
          message: `${newInv.invoiceNumber} for ${newInv.customerName} — Rs. ${newInv.grandTotal.toLocaleString()}`,
          invoiceId: newInv.id,
        });
        return newInv.id;
      },

      updateInvoice: (invId, data) => {
        set(s => ({
          invoices: s.invoices.map(inv => inv.id !== invId ? inv : { ...inv, ...data, updatedAt: now() }),
        }));
      },

      addPaymentToInvoice: (invoiceId, payment) => {
        const inv = get().invoices.find(i => i.id === invoiceId);
        if (!inv) return;
        const newPayment: InvoicePayment = { ...payment, id: genId() };
        const totalPaid = inv.totalPaid + payment.amount;
        const balance = Math.max(0, inv.grandTotal - totalPaid);
        const paymentStatus: PaymentStatus = balance <= 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'unpaid';
        const invoiceStatus = balance <= 0 ? 'paid' : inv.invoiceStatus === 'draft' ? 'sent' : inv.invoiceStatus;

        const updatedInv = {
          ...inv,
          payments: [...inv.payments, newPayment],
          totalPaid,
          balance,
          paymentStatus,
          invoiceStatus,
          updatedAt: now(),
        };
        set(s => ({ invoices: s.invoices.map(i => i.id === invoiceId ? updatedInv : i) }));

        // Auto-create finance INCOME transaction
        get().addFinanceTransaction({
          type: 'INCOME',
          amount: payment.amount,
          description: `Payment received for ${inv.invoiceNumber}`,
          date: payment.date,
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber,
          customerId: inv.customerId,
          customerName: inv.customerName,
          paymentId: newPayment.id,
          paymentMethod: payment.method,
          category: 'Invoice Payment',
          reference: payment.reference,
        });

        // Notification
        get().addNotification({
          type: paymentStatus === 'paid' ? 'invoice_paid' : 'payment_received',
          title: paymentStatus === 'paid' ? '💰 Invoice Fully Paid' : '💵 Payment Received',
          message: `Rs. ${payment.amount.toLocaleString()} received from ${inv.customerName}. Invoice ${inv.invoiceNumber}.`,
          invoiceId: inv.id,
        });

        // Auto-create warranty when fully paid
        if (paymentStatus === 'paid') {
          get().activateWarranty(invoiceId);
        }
      },

      markInvoiceComplete: (invoiceId) => {
        set(s => ({
          invoices: s.invoices.map(inv =>
            inv.id !== invoiceId ? inv : { ...inv, invoiceStatus: 'paid', paymentStatus: 'paid', updatedAt: now() }
          ),
        }));
        get().activateWarranty(invoiceId);
      },

      confirmInstallation: (invoiceId) => {
        const inv = get().invoices.find(i => i.id === invoiceId);
        if (!inv || inv.stockDeducted) return;

        // Deduct stock via StockMovements
        for (const item of inv.lineItems) {
          if (item.productId && item.itemType === 'product') {
            get().addStockMovement({
              type: 'STOCK_OUT',
              productId: item.productId,
              productName: item.name,
              quantity: item.quantity,
              date: now(),
              invoiceId: inv.id,
              invoiceNumber: inv.invoiceNumber,
              reason: `Project Installation — ${inv.invoiceNumber}`,
              performedBy: 'System',
            });
          }
        }

        set(s => ({
          invoices: s.invoices.map(i => i.id === invoiceId
            ? { ...i, stockDeducted: true, invoiceStatus: 'sent', updatedAt: now() }
            : i
          ),
        }));

        get().addNotification({
          type: 'installation_confirmed',
          title: '🔧 Installation Confirmed',
          message: `Stock deducted for invoice ${inv.invoiceNumber}. Products allocated to project.`,
          invoiceId: inv.id,
        });
      },

      // ── WARRANTY ACTIONS ───────────────────────────────────────────────────
      activateWarranty: (invoiceId) => {
        const inv = get().invoices.find(i => i.id === invoiceId);
        if (!inv) return null;
        // Don't create duplicates
        if (inv.warrantyId) return inv.warrantyId;
        const existing = get().warranties.find(w => w.invoiceId === invoiceId);
        if (existing) return existing.id;

        const installDate = inv.installationDate || inv.invoiceDate;

        // Per-product warranty: use individual line item warrantyPeriod or defaults
        const items = inv.lineItems.map(li => {
          const months = parseWarrantyMonths(li.warrantyPeriod || '24 Months');
          const expiry = new Date(installDate);
          expiry.setMonth(expiry.getMonth() + months);
          return {
            id: genId(),
            productId: li.productId,
            productName: li.name,
            modelNumber: li.modelNumber,
            quantity: li.quantity,
            warrantyPeriod: li.warrantyPeriod || '24 Months',
            warrantyExpiry: expiry.toISOString(),
          };
        });

        // Product warranty: default 24 months
        const productMonths = 24;
        const productExpiry = new Date(installDate);
        productExpiry.setMonth(productExpiry.getMonth() + productMonths);

        // Service warranty: default 12 months
        const serviceMonths = 12;
        const serviceExpiry = new Date(installDate);
        serviceExpiry.setMonth(serviceExpiry.getMonth() + serviceMonths);

        const warrantyRef = genWarrantyId();
        const newWarranty: WorkflowWarranty = {
          id: genId(),
          warrantyId: warrantyRef,
          verifyUrl: `/warranty/verify/${warrantyRef}`,
          invoiceId,
          invoiceNumber: inv.invoiceNumber,
          quotationId: inv.quotationId,
          customerId: inv.customerId,
          customerName: inv.customerName,
          customerPhone: inv.customerPhone,
          installationDate: installDate,
          productWarrantyStart: installDate,
          productWarrantyExpiry: productExpiry.toISOString(),
          productWarrantyDuration: `${productMonths} Months`,
          serviceWarrantyStart: installDate,
          serviceWarrantyExpiry: serviceExpiry.toISOString(),
          serviceWarrantyDuration: `${serviceMonths} Months`,
          items,
          warrantyStatus: 'active',
          serviceStatus: 'active',
          createdAt: now(),
          updatedAt: now(),
        };

        set(s => ({
          warranties: [newWarranty, ...s.warranties],
          invoices: s.invoices.map(i => i.id === invoiceId ? { ...i, warrantyId: newWarranty.id, updatedAt: now() } : i),
        }));

        get().addNotification({
          type: 'warranty_activated',
          title: '🛡️ Warranty Activated',
          message: `Warranty ${warrantyRef} activated for ${inv.customerName}.`,
          warrantyId: newWarranty.id,
          invoiceId: inv.id,
        });

        return newWarranty.id;
      },

      updateWarranty: (id, data) => {
        set(s => ({
          warranties: s.warranties.map(w => w.id === id ? { ...w, ...data, updatedAt: now() } : w),
        }));
      },

      getWarrantyByVerifyId: (verifyId) => {
        return get().warranties.find(w => w.warrantyId === verifyId);
      },

      // ── FINANCE TRANSACTIONS ───────────────────────────────────────────────
      addFinanceTransaction: (tx) => {
        const newTx: FinanceTransaction = {
          ...tx,
          id: genId(),
          createdAt: now(),
        };
        set(s => ({ financeTransactions: [newTx, ...s.financeTransactions] }));
        return newTx.id;
      },

      getTotalIncome: () => {
        return get().financeTransactions
          .filter(t => t.type === 'INCOME')
          .reduce((s, t) => s + t.amount, 0);
      },

      getTotalExpenses: () => {
        return get().financeTransactions
          .filter(t => t.type === 'EXPENSE')
          .reduce((s, t) => s + t.amount, 0);
      },

      getTotalMaterialCost: () => {
        // Calculate from invoices' line item cost prices
        return get().invoices
          .flatMap(inv => inv.lineItems)
          .reduce((s, li) => s + ((li.costPrice || 0) * li.quantity), 0);
      },

      getNetProfit: () => {
        const income = get().getTotalIncome();
        const expenses = get().getTotalExpenses();
        const materialCost = get().getTotalMaterialCost();
        return income - expenses - materialCost;
      },

      // ── STOCK MOVEMENTS ────────────────────────────────────────────────────
      addStockMovement: (mov) => {
        const newMov: StockMovement = { ...mov, id: genId(), createdAt: now() };
        set(s => ({ stockMovements: [newMov, ...s.stockMovements] }));
      },

      // ── NOTIFICATIONS ──────────────────────────────────────────────────────
      addNotification: (n) => {
        const newN: AdminNotification = { ...n, id: genId(), isRead: false, createdAt: now() };
        set(s => ({ notifications: [newN, ...s.notifications] }));
      },

      markNotificationRead: (notifId) => {
        set(s => ({
          notifications: s.notifications.map(n => n.id === notifId ? { ...n, isRead: true } : n),
        }));
      },

      clearAllNotifications: () => {
        set(s => ({ notifications: s.notifications.map(n => ({ ...n, isRead: true })) }));
      },

      unreadCount: () => get().notifications.filter(n => !n.isRead).length,

      // ── RESET BUSINESS DATA ────────────────────────────────────────────────
      resetBusinessData: () => {
        set({
          orders: [],
          quotations: [],
          invoices: [],
          warranties: [],
          financeTransactions: [],
          stockMovements: [],
          notifications: [],
        });
      },
    }),
    {
      name: 'ktec-workflow-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// ─── Utility ──────────────────────────────────────────────────────────────────
function parseWarrantyMonths(period: string): number {
  if (!period) return 24;
  const match = period.match(/(\d+)\s*(year|yr|month|mo)/i);
  if (!match) return 24;
  const num = parseInt(match[1]);
  const unit = match[2].toLowerCase();
  if (unit.startsWith('y')) return num * 12;
  return num;
}
